import { saveDoNotDisturb, getDoNotDisturb } from '@/services/companySettingsService'
import { ElMessage, ElMessageBox } from 'element-plus'
import './doNotDisturb.scss'

// ---------- helpers ----------

const actionOptionsConstant = [
  { label: 'Discard these messages and do not send them later', value: 'discard' },
  { label: 'Save and send all the queued messages', value: 'sendAll' }
]

const hourOptionsConstant = Array.from({ length: 12 }, (_, idx) => {
  const hour = idx + 1
  return `${hour < 10 ? '0' : ''}${hour}`
})

const minuteOptionsConstant = ['00', '15', '30', '45']

function createTimeValue(hour, minute, meridiem) {
  return { hour, minute, meridiem }
}

function createChannelDndSettings(options) {
  return {
    enabled: false,
    from: createTimeValue('09', '00', 'pm'),
    to: createTimeValue('07', '00', 'am'),
    ...options
  }
}

// Convert time to minutes
function convertTimeToMinutes(hour, minute, meridiem) {
  let hour24 = parseInt(hour, 10)

  if (meridiem === 'pm' && hour24 !== 12) {
    hour24 += 12
  } else if (meridiem === 'am' && hour24 === 12) {
    hour24 = 0
  }

  return (hour24 * 60) + parseInt(minute, 10)
}

// Map action For Send Messages
function mapAction(action) {
  const actionMap = {
    discard: 'discard',
    sendAll: 'send-later'
  }
  return actionMap[action] || action
}

// Convert minutes to time
function convertMinutesToTime(minutes) {
  const hours = Math.floor(minutes / 60) % 24
  const mins = minutes % 60

  let hour12 = hours
  let meridiem = 'am'

  if (hours === 0) {
    hour12 = 12
    meridiem = 'am'
  } else if (hours === 12) {
    hour12 = 12
    meridiem = 'pm'
  } else if (hours > 12) {
    hour12 = hours - 12
    meridiem = 'pm'
  } else {
    hour12 = hours
    meridiem = 'am'
  }

  return {
    hour: `${hour12 < 10 ? '0' : ''}${hour12}`,
    minute: `${mins < 10 ? '0' : ''}${mins}`,
    meridiem
  }
}

function mapActionFromBackend(action) {
  const actionMap = {
    discard: 'discard',
    'send-later': 'sendAll'
  }
  return actionMap[action] || action
}

function extractChannelKey(channelDndSettingsKey) {
  // Extract "push" from "campaign-dnd-push"
  return channelDndSettingsKey.replace('campaign-dnd-', '')
}

function prepareChannelDndSettingsPayload(channel) {
  return {
    key: `campaign-dnd-${channel.key}`,
    value: {
      enabled: channel.enabled,
      from: convertTimeToMinutes(channel.from.hour, channel.from.minute, channel.from.meridiem),
      to: convertTimeToMinutes(channel.to.hour, channel.to.minute, channel.to.meridiem),
      action: mapAction(channel.action)
    }
  }
}

// ---------- component (Options API, similar to GeneralCampaignSettings.js) ----------

export default {
  name: 'DoNotDisturb',

  data() {
    const channels = [
      createChannelDndSettings({
        key: 'email',
        label: 'Email',
        iconClass: 'fa-solid fa-envelope',
        description: 'Promotional, transactional or automated emails',
        action: 'discard',
        badgeBg: '#ede9fe',
        badgeColor: '#6d28d9'
      }),
      createChannelDndSettings({
        key: 'whatsapp',
        label: 'WhatsApp',
        iconClass: 'fab fa-whatsapp',
        description: 'Notifications and promotions over WhatsApp',
        action: 'discard',
        badgeBg: '#ecfdf5',
        badgeColor: '#047857'
      }),
      createChannelDndSettings({
        key: 'sms',
        label: 'SMS, RCS & Connectors',
        iconClass: 'fa-solid fa-comment-dots',
        description: 'Text messages, RCS conversations and connected partners',
        enabled: true,
        action: 'discard',
        badgeBg: '#e0f2fe',
        badgeColor: '#0ea5e9'
      }),
      createChannelDndSettings({
        key: 'push',
        label: 'Push',
        iconClass: 'fa-solid fa-mobile-screen-button',
        description: 'Push notifications sent via mobile apps',
        action: 'discard',
        badgeBg: '#fdf5e6',
        badgeColor: '#b45309'
      })
    ]

    const channelSavingStates = {}
    channels.forEach(channel => {
      channelSavingStates[channel.key] = false
    })

    return {
      loading: false,
      channels,
      channelSavingStates,
      actionOptions: actionOptionsConstant,
      hourOptions: hourOptionsConstant,
      minuteOptions: minuteOptionsConstant
    }
  },

  created() {
    this.fetchDoNotDisturbSettings()
  },

  methods: {
    // UI helpers
    setMeridiem(timeObj, value) {
      timeObj.meridiem = value
    },

    onToggleChannel(channel) {
      // Channel toggle handler
    },

    // API helpers
    async fetchDoNotDisturbSettings() {
      try {
        this.loading = true

        const fetchChannelDndSettings = this.channels.map(async (channel) => {
          const channelKey = `campaign-dnd-${channel.key}`
          try {
            const response = await getDoNotDisturb(channelKey)
            return {
              key: channelKey,
              value: response.data || null
            }
          } catch (error) {
            console.error(`Error fetching ${channelKey}:`, error)
            return {
              key: channelKey,
              value: null
            }
          }
        })

        const channelDndSettings = await Promise.all(fetchChannelDndSettings)
        const channelDndSettingsArray = channelDndSettings.filter(item => item.value !== null)

        channelDndSettingsArray.forEach(item => {
          if (!item || !item.key) return

          const channelKey = extractChannelKey(item.key)
          const channel = this.channels.find(c => c.key === channelKey)

          if (channel && item.value) {
            const { from, to, action, enabled } = item.value

            if (from !== undefined && from !== null) {
              const fromTime = convertMinutesToTime(from)
              channel.from.hour = fromTime.hour
              channel.from.minute = fromTime.minute
              channel.from.meridiem = fromTime.meridiem
            }

            if (to !== undefined && to !== null) {
              const toTime = convertMinutesToTime(to)
              channel.to.hour = toTime.hour
              channel.to.minute = toTime.minute
              channel.to.meridiem = toTime.meridiem
            }

            if (action !== undefined && action !== null) {
              const mappedAction = mapActionFromBackend(action)
              // Only set if it's a valid action (discard or sendAll)
              if (mappedAction === 'discard' || mappedAction === 'sendAll') {
                channel.action = mappedAction
              } else {
                channel.action = 'discard'
              }
            } else {
              // Default to discard if no action is provided
              channel.action = 'discard'
            }

            if (enabled !== undefined) {
              channel.enabled = enabled
            } else if (from !== undefined || to !== undefined) {
              channel.enabled = true
            }
          }
        })

        return true
      } catch (error) {
        console.error('Error fetching Do Not Disturb settings:', error)
        return false
      } finally {
        this.loading = false
      }
    },

    async saveChannelDndSettings(channel) {
      try {
        this.channelSavingStates[channel.key] = true

        const payload = prepareChannelDndSettingsPayload(channel)
        await saveDoNotDisturb(payload)

        await this.fetchDoNotDisturbSettings()

        ElMessage({
          showClose: true,
          type: 'success',
          message: `${channel.label} settings saved successfully.`
        })

        return true
      } catch (error) {
        console.error('Error saving channel settings:', error)
        ElMessageBox.alert("Something's not right! Please contact support.", 'Error', {
          confirmButtonText: 'OK'
        })
        return false
      } finally {
        this.channelSavingStates[channel.key] = false
      }
    },
    // Methods used directly in template (match previous names)
    handleSaveChannelDndSettings(channel) {
      this.saveChannelDndSettings(channel)
    }
  }
}
